import React, { useEffect, useMemo, useState } from 'react';
import logger, {
  clearLogs as clearLogStore,
  getLogs as getStoredLogs,
  subscribeToLogs,
} from '../logger';
import { SHOW_MOBILE_LOG_DRAWER } from '../constants';

const formatTimestamp = (isoString) => {
  if (!isoString) {
    return '';
  }
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (_error) {
    return isoString;
  }
};

const levelStyles = {
  log: { color: '#e2e8f0' },
  info: { color: '#bae6fd' },
  warn: { color: '#facc15' },
  error: { color: '#f87171' },
  debug: { color: '#94a3b8' },
};

const containerStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9998,
  transformOrigin: 'bottom',
  transition: 'transform 0.3s ease',
  pointerEvents: 'none',
};

const panelStyle = {
  margin: '0 auto',
  maxWidth: '960px',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  backgroundColor: '#0f172a',
  color: '#f8fafc',
  boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.45)',
  height: '55vh',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(148, 163, 184, 0.25)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
};

const actionsStyle = {
  display: 'flex',
  gap: '8px',
};

const logListStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 16px 16px',
  fontFamily: 'Menlo, Consolas, Monaco, monospace',
  fontSize: '12px',
  lineHeight: 1.5,
};

const emptyStateStyle = {
  marginTop: '64px',
  textAlign: 'center',
  color: 'rgba(226, 232, 240, 0.65)',
  fontSize: '14px',
};

const toggleButtonBase = {
  position: 'fixed',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  border: 'none',
  borderRadius: '9999px',
  padding: '10px 16px',
  fontSize: '14px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#0f172a',
  color: '#f8fafc',
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.45)',
  cursor: 'pointer',
  zIndex: 9999,
};

const dotStyle = {
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '9999px',
  backgroundColor: '#22d3ee',
};

const buttonStyle = {
  border: '1px solid rgba(148, 163, 184, 0.4)',
  backgroundColor: 'rgba(15, 23, 42, 0.7)',
  borderRadius: '8px',
  color: '#e2e8f0',
  padding: '6px 10px',
  fontSize: '12px',
  cursor: 'pointer',
};

const closeButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'rgba(30, 41, 59, 0.9)',
};

const copyStatusStyles = {
  success: { color: '#34d399' },
  error: { color: '#f87171' },
  idle: { color: '#94a3b8' },
};

const isBrowser = typeof window !== 'undefined';

const copyToClipboard = async (text) => {
  if (!isBrowser) return false;

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  let success = false;
  try {
    success = document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }

  return success;
};

const LoggingDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState(() => getStoredLogs());
  const [hasUnread, setHasUnread] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle');

  useEffect(() => {
    const unsubscribe = subscribeToLogs((entries) => {
      setLogs(entries);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isOpen && logs.length > 0) {
      setHasUnread(true);
    }
  }, [logs, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
    }
  }, [isOpen]);

  const formattedLogs = useMemo(
    () =>
      logs
        .slice()
        .reverse()
        .map((entry) => ({
          ...entry,
          timestampDisplay: formatTimestamp(entry.timestamp),
        })),
    [logs],
  );

  const copyPayload = useMemo(
    () =>
      logs
        .map(
          (entry) =>
            `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}`,
        )
        .join('\n'),
    [logs],
  );

  const handleCopy = async () => {
    if (!copyPayload) {
      setCopyStatus('idle');
      return;
    }

    try {
      await copyToClipboard(copyPayload);
      setCopyStatus('success');
    } catch (error) {
      setCopyStatus('error');
      logger.error('[LoggingDrawer] Failed to copy logs to clipboard', error);
    }

    setTimeout(() => {
      setCopyStatus('idle');
    }, 2000);
  };

  const handleClear = () => {
    clearLogStore();
  };

  if (!isBrowser || !SHOW_MOBILE_LOG_DRAWER) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        style={{
          ...toggleButtonBase,
          backgroundColor: hasUnread ? '#0ea5e9' : toggleButtonBase.backgroundColor,
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>Logs</span>
        <span
          style={{
            fontSize: '12px',
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            padding: '2px 8px',
            borderRadius: '9999px',
          }}
        >
          {logs.length}
        </span>
        {hasUnread && <span style={dotStyle} />}
      </button>

      <div
        style={{
          ...containerStyle,
          transform: isOpen ? 'translateY(0)' : 'translateY(calc(100% + 16px))',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        <div style={panelStyle}>
          <div style={headerStyle}>
            <div>
              <strong>App Logs</strong>
              <span style={{ marginLeft: '8px', color: '#94a3b8', fontSize: '12px' }}>
                Tap entries to expand
              </span>
            </div>
            <div style={actionsStyle}>
              <button type="button" style={buttonStyle} onClick={handleCopy}>
                {copyStatus === 'success'
                  ? 'Copied'
                  : copyStatus === 'error'
                  ? 'Copy failed'
                  : 'Copy'}
              </button>
              <button type="button" style={buttonStyle} onClick={handleClear}>
                Clear
              </button>
              <button
                type="button"
                style={closeButtonStyle}
                onClick={() => setIsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
          <div
            style={{
              padding: '0 16px 8px',
              minHeight: '20px',
              fontSize: '12px',
              color: copyStatusStyles[copyStatus]?.color,
            }}
          >
            {copyStatus === 'success' && 'Copied logs to clipboard'}
            {copyStatus === 'error' && 'Unable to copy logs. See log drawer for errors.'}
          </div>
          <div style={logListStyle}>
            {formattedLogs.length === 0 ? (
              <div style={emptyStateStyle}>No logs yet. Actions will appear here.</div>
            ) : (
              formattedLogs.map((entry) => (
                <details
                  key={entry.id}
                  open={entry.level === 'error' || entry.level === 'warn'}
                  style={{
                    marginBottom: '12px',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
                    paddingBottom: '12px',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      listStyle: 'none',
                    }}
                  >
                    <span style={{ ...levelStyles[entry.level], flexShrink: 0 }}>
                      {entry.level.toUpperCase()}
                    </span>
                    <span style={{ color: '#94a3b8', flexShrink: 0 }}>
                      {entry.timestampDisplay}
                    </span>
                    <span
                      style={{
                        color: '#e2e8f0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {entry.message}
                    </span>
                  </summary>
                  <pre
                    style={{
                      marginTop: '8px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#cbd5f5',
                    }}
                  >
                    {entry.message}
                  </pre>
                </details>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoggingDrawer;
